import React, { useEffect, useRef, useState } from "react";
import DeckGL from "@deck.gl/react/typed";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  BitmapLayer,
  GeoJsonLayer,
  GridCellLayer,
  IconLayer,
  PathLayer,
} from "@deck.gl/layers/typed";
import {
  StaticMap,
  MapContext,
  NavigationControl,
  Marker,
  MapRef,
  ViewState,
} from "react-map-gl";
import { Box, Fab, Typography, useTheme } from "@mui/material";
import { TileLayer } from "@deck.gl/geo-layers/typed";

import {
  selectAircraftVitals,
  selectLive,
} from "../../features/aircraft/aircraftSelectors";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  cellData,
  getUavIcon,
  uavImageColours,
  uavOtherImageColours,
  uavStateColour,
} from "../../features/aircraft/state";
import {
  selectGeofences,
  selectLandingZone,
  selectPads,
} from "../../features/airspace/airspaceSlice";
import { selectCell, selectRegion } from "../../features/session/sessionSlice";
import { ReactComponent as Navigation } from "../../assets/svg/Navigation.svg";
import { getLandingZoneListThunk } from "../../features/airspace/airspaceSlice";
import { Clock } from "../../components/Clock";

function hexToRgb(hex: string): [number, number, number] {
  // Expand short-form hex color if needed (e.g., #f06 -> #ff0066)
  if (hex?.length === 4) {
    hex = hex.replace(/([0-9a-f])/gi, "$1$1");
  }

  // Extract the red, green, and blue components from the hex color
  const rgb = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);

  if (rgb) {
    return [parseInt(rgb[1], 16), parseInt(rgb[2], 16), parseInt(rgb[3], 16)];
  } else {
    return [0, 0, 0];
  }
}

const MAP_STYLE = "mapbox://styles/mapbox/dark-v9";

export const MapboxMap = (props: { showTiles: boolean }) => {
  const live = useAppSelector(selectAircraftVitals);
  const relay = useAppSelector(selectLive);
  const geofences = useAppSelector(selectGeofences);
  const mapRef = useRef<MapRef | null>(null);
  const pads = useAppSelector(selectPads);
  const theme = useTheme();
  const cell = useAppSelector(selectCell);
  const landingZones = useAppSelector(selectLandingZone);
  const [hoverInfo, setHoverInfo] = useState<any>();
  const fabRef = React.useRef<HTMLButtonElement | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
    }
  }, [mapRef.current]);

  const displayAltitude = (alt: number | null | undefined) => {
    if (!alt) return "0";
    if (alt < 0) return "0";
    return alt.toFixed(0);
  };

  const [initialViewState, setInitialViewState] = useState<ViewState>({
    latitude:
      cell?.centerLatitude || cellData[cell?.cellName || ""]?.latLon?.[0],
    longitude:
      cell?.centerLongitude || cellData[cell?.cellName || ""]?.latLon?.[1],
    zoom: 11,
    bearing: 0,
    pitch: 0,
  });

  useEffect(() => {
    const loc = cellData[cell?.cellName || ""];
    setInitialViewState({
      latitude: cell?.centerLatitude || loc?.latLon?.[0] || 0,
      longitude: cell?.centerLongitude || loc?.latLon?.[1] || 0,
      zoom: 14,
      pitch: 0,
      bearing: 0,
    });
  }, [cell]);

  useEffect(() => {
    console.log("Cell changed, fetching landing zones");
    if (cell) {
      dispatch(getLandingZoneListThunk({}));
    }
  }, [cell, dispatch]);

  const onClickCentre = () => {
    const loc = cellData[cell?.cellName || ""];
    setInitialViewState({
      latitude: cell?.centerLatitude || loc?.latLon?.[0] || 0,
      longitude: cell?.centerLongitude || loc?.latLon?.[1] || 0,
      zoom: 14,
      pitch: 0,
      bearing: 0,
      // @ts-ignore
      transitionDuration: Math.random(),
      // workaround, for some reason the transitionDuration needs to change every time to work.
    });
  };

  const onClickFab = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onClickCentre();
  };

  useEffect(() => {
    const handleTouch = (e: TouchEvent) => {
      e.preventDefault();
      onClickCentre();
    };

    const element = fabRef.current;
    element?.addEventListener("touchstart", handleTouch, { passive: false });

    return () => {
      element?.removeEventListener("touchstart", handleTouch);
    };
  }, []);
  return (
    <DeckGL
      initialViewState={{ ...initialViewState, minZoom: 12, maxZoom: 20 }}
      controller={{
        touchRotate: false,
        dragRotate: false,
        keyboard: {
          rotateSpeedX: 0,
          rotateSpeedY: 0,
        },
      }}
      layers={[
        props.showTiles
          ? new TileLayer({
              id: "satellite-tiles2",
              data: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
              minZoom: 0,
              maxZoom: 19,
              tileSize: 256,
              renderSubLayers: (props: any) => {
                const {
                  bbox: { west, south, east, north },
                } = props.tile;
                return new BitmapLayer(props, {
                  // @ts-ignore
                  data: null,
                  image: props.data,
                  bounds: [west, south, east, north],
                });
              },
            })
          : null,
        props.showTiles
          ? new TileLayer({
              id: "satellite-tiles",
              data: "https://tiles.manna.aero/{z}/{x}/{y}.png",
              minZoom: 12,
              maxZoom: 22,
              tileSize: 256,
              renderSubLayers: (props: any) => {
                const {
                  bbox: { west, south, east, north },
                } = props.tile;
                return new BitmapLayer(props, {
                  // @ts-ignore
                  data: null,
                  image: props.data,
                  bounds: [west, south, east, north],
                });
              },
            })
          : null,
        geofences.map((fence) => {
          return new GeoJsonLayer({
            id: "path-layer-fences" + fence.id,
            data: fence.geoJson,
            getWidth: 2,
            widthUnits: "pixels",
            pickable: true,
            lineWidthScale: 0.5,
            lineWidthMinPixels: 0.5,
            getLineColor: [255, 255, 255],
            filled: false,
            getPointRadius: 0.5,
            getLineWidth: 8,
            opacity: 1,
          });
        }),
        landingZones.map((fence) => {
          return new GeoJsonLayer({
            id: "path-layer-fences" + fence.id,
            data: fence.geoJson,
            getWidth: 2,
            widthUnits: "pixels",
            pickable: true,
            lineWidthScale: 0.5,
            lineWidthMinPixels: 0.5,
            getLineColor: [255, 255, 255],
            filled: false,
            getPointRadius: 0.5,
            getLineWidth: 8,
            opacity: 1,
          });
        }),

        new GridCellLayer({
          id: "grid-cell-layer",
          data: pads,
          pickable: true,
          extruded: true,
          cellSize: 10,
          elevationScale: 10,
          getPosition: (d) => [d.longitude, d.latitude],
          getFillColor: (d) => [255, 255, 255],
          getElevation: 0,
          offset: [-0, -0],
          onHover: (info) => setHoverInfo(info),
        }),
        live.map((aircraft) => {
          if (!aircraft || !aircraft.mission?.geoJson) return null;
          const inEmergency = aircraft.uavSubState?.includes("STATE_FLIGHT_EOPS") ?? false;
          const colour = uavStateColour(
            aircraft.uavState || "",
            aircraft.flightMode || "",
            false,
            false,
            inEmergency
          );
          return new GeoJsonLayer({
            id: "path-layer" + aircraft.uavId,
            data: aircraft.mission?.geoJson,
            getWidth: 2,
            widthUnits: "pixels",
            pickable: true,
            lineWidthScale: 1,
            lineWidthMinPixels: 1,
            getLineColor: hexToRgb(colour.route),
            getPointRadius: 100,
            getLineWidth: 1,
            getElevation: 2000,
            lineCapRounded: true,
          });
        }),

        new PathLayer({
          id: "path-layer-2",
          data: Object.values(relay).map((aircraft) => {
            if (!aircraft || !aircraft.mission?.geoJson) return null;
            const inEmergency = aircraft.uavSubState?.includes("STATE_FLIGHT_EOPS") ?? false;
            const colour = uavStateColour(
              aircraft.uavState || "",
              aircraft.flightMode || "",
              false,
              false,
              inEmergency
            );

            return {
              path:
                aircraft.mission?.geoJson?.features.map((f) => {
                  return f?.geometry?.coordinates;
                }) || [],
              color: hexToRgb(colour.route),
            };
          }),
          getPath: (d) => d?.path,
          getWidth: 2,
          widthUnits: "pixels",
          pickable: true,
          lineWidthScale: 1,
          lineWidthMinPixels: 1,
          getColor: (d) => d?.color,

          getPointRadius: 100,
          getLineWidth: 1,
          getElevation: 2000,
        }),
        new IconLayer({
          id: "icon-layer",
          data: live.map((aircraft) => {
            if (!aircraft) return null;
            const inEmergency = aircraft.uavSubState?.includes("STATE_FLIGHT_EOPS") ?? false;
            const icon =
              uavImageColours[
                getUavIcon(aircraft.uavState || "", aircraft.flightMode || "", inEmergency)
              ];
            const icon2 =
              uavOtherImageColours[
                getUavIcon(aircraft.uavState || "", aircraft.flightMode || "", inEmergency)
              ];
            return {
              coordinates: [aircraft.lon, aircraft.lat],
              heading: aircraft.heading,
              size: 64,
              controlled: aircraft?.controlled,
              avatar_url: aircraft?.controlled ? icon : icon2,
            };
          }),
          getIcon: (d) => ({
            url: d.avatar_url,
            width: d.size,
            height: d.size,
          }),
          getSize: (d) => (d?.controlled ? 1.5 : 0.8),
          pickable: false,
          visible: true,
          sizeScale: 24,

          getPosition: (d) => d.coordinates,
          getAngle: (d) => -d.heading,
          getElevation: 5000,
        }),
      ]}
      // @ts-ignore
      ContextProvider={MapContext.Provider}
    >
      <StaticMap
        ref={mapRef}
        mapStyle={MAP_STYLE}
        mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
      />
      <NavigationControl
        style={{ position: "absolute", top: 10, left: 10 }}
        showCompass={false}
      />
      <Box style={{ position: "absolute", top: 10, left: 50 }}>
        <Clock />
        <Typography
          variant={"h4"}
          fontSize={15}
          fontWeight={600}
          textAlign={"start"}
        >
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'short', 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          })}
        </Typography>
        <Typography
          variant={"h4"}
          fontSize={15}
          fontWeight={600}
          textAlign={"start"}
        >
          v{process.env.REACT_APP_BASE_HASH || ""}
        </Typography>
        <Typography
          variant={"h4"}
          fontSize={15}
          fontWeight={600}
          textAlign={"start"}
        >
          {cell?.cellName || ""}
        </Typography>
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 72,
          borderRadius: 0,
          bgcolor: "transparent",
          zIndex: 500,
          pointerEvents: "none",
        }}
      >
        <Box sx={{ pointerEvents: "none" }}>
          <Fab
            sx={{
              top: 16,
              right: 16,
              pointerEvents: "all",
              backgroundColor: theme.palette.background.paper,
            }}
            color={"default"}
            ref={fabRef}
            onClick={onClickFab}
          >
            <Navigation stroke={theme.palette.text.primary} />
          </Fab>
        </Box>
      </Box>
      {live.map((aircraft) => {
        if (!aircraft) return null;
        return (
          <Marker
            key={`marker-${aircraft.uavId}`}
            longitude={aircraft.lon || 0}
            latitude={aircraft.lat || 0}
            offsetTop={16}
            offsetLeft={-52}
            style={{ zIndex: 0 }}
          >
            <Box
              display={"flex"}
              sx={{
                flexDirection: "column",
                borderRadius: 2,
                width: 101,
                boxSizing: "border-box",
              }}
            >
              <Box
                display={"flex"}
                flex={1}
                sx={{}}
                alignItems="center"
                justifyItems="center"
              >
                <Typography
                  color={"white"}
                  sx={{
                    width: "100%",
                    textJustify: "center",
                    textAlign: "center",
                    fontSize: 14,
                    textShadow: "black 2px 5px 4px",
                    fontWeight: 900,
                  }}
                >{`${aircraft.uavId}`}</Typography>
              </Box>

              <Box
                display={"flex"}
                flex={1}
                sx={{}}
                alignItems="center"
                justifyItems="center"
              >
                <Typography
                  sx={{
                    width: "100%",
                    textJustify: "center",
                    textAlign: "center",
                    textShadow: "black 2px 5px 4px",

                    fontSize: 14,
                    fontWeight: 900,
                  }}
                  color={"white"}
                >{`${displayAltitude(aircraft.altitude)}m`}</Typography>
              </Box>
            </Box>
          </Marker>
        );
      })}
      {hoverInfo && (
        <Box
          style={{
            position: "absolute",
            zIndex: 1,
            pointerEvents: "none",
            left: hoverInfo?.x + 8,
            top: hoverInfo?.y + 12,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ textShadow: "black 2px 5px 4px" }}
          >
            {hoverInfo?.object?.name}
          </Typography>
        </Box>
      )}
    </DeckGL>
  );
};
